import { Injectable } from '@angular/core';
import { HttpRequestPayload, IHttpErrorPayload, IHttpSuccessPayload } from '@core/models';
import { Observable, of, throwError } from 'rxjs';
import { ICreditCardDetails, ICreditCardTransactionStatement, ICreditCardSummaryData, ICreditCardFeeDetails } from '../common/models/credit-card.model';
import { HttpProviderService } from '../core/services/http-provider/http-provider.service';
import { ProvidehttpService } from '../providehttp.service';
import { catchError, map } from 'rxjs/operators';
import { IContextualMenu } from '../common/models/contextual-menu.model';
import { CustomerService } from './customer.service';
import { ECreditcardServiceCode } from '@common/models';

@Injectable({
  providedIn: 'root'
})

export class CreditCardService {
  popupData:any;
  contextualMenuList: IContextualMenu[] = [
    {
      id: "CC003",
      icon: "credit-card-easy-loan",
      name: "Easy loan",
      title: "Easy Loan",
      path: ["shell", "credit-cards", "easyloan"],
      serviceCode: ECreditcardServiceCode.EASY_LOAN_CREDITCARD
    },
    {
      id: "CC004",
      icon: "credit-card-payment",
      name: "Pay bill",
      title: "Credit Card Pay Bill",
      path: ["shell", "credit-cards", "paybill"],
      serviceCode: ECreditcardServiceCode.PAYBILL_CREDITCARD
    },
    {
      id: "CC005",
      icon: "credit-card-manage-service",
      name: "Manage services",
      title: "Manage Services",
      path: ["shell", "credit-cards", "manage-service"],
      serviceCode: ECreditcardServiceCode.MANAGE_CREDITCARD
    },
    {
      id: "CC006",
      icon: "credit-card-block",
      name: "Block / Unblock",
      title: "Credit card(Block / Unblock)",
      path: ["shell", "credit-cards", "manage-creditcard-status"],
      serviceCode: ECreditcardServiceCode.BLOCK_UNBLOCK_CREDITCARD
    },
    {
      id: "CC008",
      icon: "credit-card-balance-transfer",
      name: "Balance transfer",
      title: "Credit card Balance Transfer",
      path: ["shell", "credit-cards", "balance-transfer"],
      serviceCode: ECreditcardServiceCode.BALANCE_TRANSFER_CREDITCARD
    },
    {
      id: "CC009",
      icon: "credit-card-replacement",
      name: "Replace/Renew <em>card</em>",
      title: "Credit Card (Replace/Renew)",
      path: ["shell", "credit-cards", "replace-card"],
      serviceCode: ECreditcardServiceCode.REPLACE_CREDITCARD
    },
    {
      id: "CC010",
      icon: "credit-card-change-pin",
      name: "Set/Change <em>card PIN</em>",
      title: "Manage credit card PIN",
      path: ["shell", "credit-cards", "change-pin"],
      serviceCode: ECreditcardServiceCode.CHANGE_PIN_CREDITCARD
    },
    {
      id: "CC011",
      icon: "credit-card-limit-increase",
      name: "Request for <em>Limit Increase</em>",
      title: "Request for Limit Increase",
      path: ["shell", "credit-cards", "limit-increase"],
      serviceCode: ECreditcardServiceCode.LIMIT_INCREASE_CREDITCARD
    }
  ];

  private cardDetails: Map<string, ICreditCardDetails> = new Map<string, ICreditCardDetails>();
  private cardTransactionStatements: Map<string, ICreditCardTransactionStatement[]> = new Map<string, ICreditCardTransactionStatement[]>();
  casaTrxFilterData: {accountNumber:string,filterPayload:any}[] = [];
  constructor(private httpService: ProvidehttpService, private _httpProvider: HttpProviderService, private _customerService: CustomerService) { }

  getMenuList(): IContextualMenu[] {
    return this.contextualMenuList;
  }

  getSelectedMenuData(menuId: string) {
    const selectedMenuItem = this.contextualMenuList.find(item => item.id == menuId);
    return selectedMenuItem;
  }

  /**
   * @description Fetch credit card statement 
   * @param cardNumber 
   */
  fetchCreditCardStement(cardRefNumber: string) {
    const httpRequest: HttpRequestPayload = {
      resource: "/creditcard/statement/" + cardRefNumber,
      method: "GET",
      queryParams: [
        {
          key: "startDate",
          value: "2022-05-10"
        },
        {
          key: "endDate",
          value: "2022-05-20"
        }
      ]
    }

    return this._httpProvider.invokeApi(httpRequest).pipe(
      map((res: IHttpSuccessPayload<any>) => {
        this.cardTransactionStatements[cardRefNumber] = (res.body && res.body['creditcard']) ? res.body['creditcard'] : [];
        return this.cardTransactionStatements[cardRefNumber];
      }),
      catchError((err: IHttpErrorPayload) => {
        return throwError(err)
      }));
  }

  /**
   * 
   * @description To fetch credit card Fee
   * @param cardRefNumber 
   * @returns 
   */
  fetchFeeDetail(cardRefNumber: string): Observable<ICreditCardFeeDetails[]> {
    const httpRequestPayload: HttpRequestPayload = {
      method: "GET",
      resource: "/cc/fee/details/" + cardRefNumber,
      queryParams: [
        {
          key: 'eventType',
          value: 'Other',
        },
      ],
    };
    return this._httpProvider.invokeApi(httpRequestPayload).pipe(
      map(
        (response: IHttpSuccessPayload<any>) => {
          return response.body.creditCard;
        },
        catchError((err: IHttpErrorPayload) => {
          return throwError(err);
        })
      )
    );
  }

  /**
   * 
   * @param cardRefNumber 
   * @returns ICreditCardDetails[]
   */
  fetchCreditCardDetails(cardRefNumber: string): Observable<ICreditCardDetails> {
    const httpRequest: HttpRequestPayload = {
      resource: "/creditcard/" + cardRefNumber,
      method: "GET"
    }

    return this._httpProvider.invokeApi(httpRequest).pipe(
      map((response: IHttpSuccessPayload<any>) => {
        this.cardDetails.set(cardRefNumber, response.body.creditcard);
        return response.body.creditcard;
      }),
      catchError((err: IHttpErrorPayload) => {
        return throwError(err)
      }));
  }

  /**
   * 
   * @param cardDetails 
   * @returns 
   */
  fetchCreditCardUtilizationTrend(cardDetails: ICreditCardSummaryData) {
    const httpRequest: HttpRequestPayload = {
      resource: "/ccutilizationtrend/" + cardDetails.contractNumber,
      queryParams: [
        {
          key: "startDate",
          value: "2022-01-01"
        },
        {
          key: "endDate",
          value: "2022-07-31"
        },
        {
          key: "outstandingAmount",
          value: cardDetails.outstandingAmount
        }
      ],
      method: "GET"
    }

    return this._httpProvider.invokeApi(httpRequest).pipe(
      map((response: IHttpSuccessPayload<any>) => {
        return response.body.utilizationTrend;
      }),
      catchError((err: IHttpErrorPayload) => {
        return throwError(err);
      }));
  }

  getCreditCardSummayData(cardRefNumber: string): ICreditCardSummaryData {
    let cardData: ICreditCardSummaryData = this._customerService.getCreditCardSummary().find(item => item.contractNumber == cardRefNumber);
    return cardData;
  }

  /**
   * 
   * @param cardRefNumber 
   * @returns 
   */
  getSummay(cardRefNumber: string): ICreditCardSummaryData {
    let cardData: ICreditCardSummaryData = this._customerService.getCreditCardSummary().find(item => item.contractNumber == cardRefNumber);
    return cardData;
  }

  /**
   * 
   * @param cardRefNumber 
   * @returns ICreditCardDetails
   */
  getDetails(cardRefNumber: string): ICreditCardDetails {
    if (this.cardDetails.has(cardRefNumber)) {
      return this.cardDetails.get(cardRefNumber);
    }
    return null;
  }

  getCardNumber(cardRefNumber: string): string {
    const cardDetails = this._customerService.getCreditCardSummary().find(item => item.contractNumber == cardRefNumber);
    return cardDetails.cardNumber;
  }

  /**
   * 
   * @param cardRefNumber 
   * @param key 
   * @returns 
   */
  getProperty(cardRefNumber: string, key: string) {
    const cardDetails = this.getDetails(cardRefNumber);
    return cardDetails[key];
  }

  /**
   * 
   * @returns 
   */
  getInactiveCards(): ICreditCardSummaryData[] {
    return this._customerService.getCreditCardSummary().filter(item => item.status == 'Inactive');
  }
}
